<script setup lang="ts">
import { computed } from 'vue';
import Paginator from 'primevue/paginator';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { formatLargeNumber } from '@/utils/formatters';

const props = withDefaults(
    defineProps<{
        totalRecords: number;
        rows: number;
        page: number; // 1-indexed
        rowsPerPageOptions?: number[];
        loading?: boolean;
    }>(),
    { rowsPerPageOptions: () => [10, 20, 50, 100], loading: false }
);

const emit = defineEmits<{
    (e: 'page-change', page: number): void;
    (e: 'rows-change', rows: number): void;
}>();

const first = computed(() => (props.page - 1) * props.rows);

const rangeText = computed(() => {
    const start = first.value + 1;
    const end = Math.min(first.value + props.rows, props.totalRecords);
    return `${start}-${end} de ${formatLargeNumber(props.totalRecords)}`;
});

const onPage = (event: { page: number }) => {
    emit('page-change', event.page + 1);
};

const onRowsChange = (e: Event) => {
    const val = (e.target as HTMLSelectElement).value;
    const n = parseInt(val, 10);
    if (!Number.isNaN(n)) emit('rows-change', n);
};

const rowOptions = computed(() => props.rowsPerPageOptions);
</script>

<template>
    <div class="flex flex-col md:flex-row items-center gap-3 py-2 px-5 bg-surface-0 border-t border-surface-200">
        <!-- Left -->
        <div class="text-xs text-surface-500 font-medium hidden md:block flex-1 min-w-0">
            Mostrando <span class="text-surface-900 font-bold">{{ rangeText }}</span> itens
        </div>

        <!-- Center: Paginator (First, Prev, Page links, Next, Last) -->
        <div
            class="app-pagination-paginator flex flex-1 justify-center min-w-0 order-first md:order-none md:flex-initial transition-opacity duration-200"
            :class="loading ? 'opacity-60 pointer-events-none' : ''"
        >
            <Paginator
                :first="first"
                :rows="rows"
                :totalRecords="totalRecords"
                :rowsPerPageOptions="rowOptions"
                @page="onPage"
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                class="!bg-transparent !border-0 !p-0"
            >
                <template #firstpagelinkicon><BaseIcon size="sm">first_page</BaseIcon></template>
                <template #prevpagelinkicon><BaseIcon size="sm">chevron_left</BaseIcon></template>
                <template #nextpagelinkicon><BaseIcon size="sm">chevron_right</BaseIcon></template>
                <template #lastpagelinkicon><BaseIcon size="sm">last_page</BaseIcon></template>
            </Paginator>
        </div>

        <!-- Right: rows per page -->
        <div
            class="flex flex-1 justify-end min-w-0 items-center gap-2 transition-opacity duration-200"
            :class="loading ? 'opacity-60 pointer-events-none' : ''"
        >
            <span class="text-xs text-surface-500 whitespace-nowrap">Linhas</span>
            <select
                :value="rows"
                :disabled="loading"
                class="h-8 min-h-8 w-20 rounded-lg border border-surface-200 bg-surface-50 px-2 text-sm text-surface-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-70 disabled:cursor-not-allowed"
                aria-label="Linhas por página"
                @change="onRowsChange"
            >
                <option v-for="opt in rowsPerPageOptions" :key="opt" :value="opt">
                    {{ opt }}
                </option>
            </select>
        </div>
    </div>
</template>

<style scoped>
.app-pagination-paginator :deep(.p-paginator) {
    gap: 0.25rem;
    background: transparent;
    border: 0;
    padding: 0;
}
.app-pagination-paginator :deep(.p-paginator-page),
.app-pagination-paginator :deep(.p-paginator-next),
.app-pagination-paginator :deep(.p-paginator-last),
.app-pagination-paginator :deep(.p-paginator-first),
.app-pagination-paginator :deep(.p-paginator-prev) {
    min-width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    transition: all 0.2s;
    color: var(--p-surface-600);
}
.app-pagination-paginator :deep(.p-paginator-page:hover),
.app-pagination-paginator :deep(.p-paginator-next:hover),
.app-pagination-paginator :deep(.p-paginator-last:hover),
.app-pagination-paginator :deep(.p-paginator-first:hover),
.app-pagination-paginator :deep(.p-paginator-prev:hover) {
    background: var(--p-surface-100);
    border-color: var(--p-surface-200);
}
.app-pagination-paginator :deep(.p-paginator-page.p-highlight) {
    background: var(--p-primary-50);
    color: var(--p-primary-700);
    font-weight: 700;
    border-color: var(--p-primary-200);
}
</style>
